import React, { useEffect, useState, useCallback } from 'react';
import {
    Text,
    Box,
    Loader,
    Menu,
    Group,
} from '@mantine/core';
import { Card, Button } from '@xplatform/design-system';
import {
    IconDownload,
    IconTable,
    IconBraces,
    IconChevronDown,
} from '@tabler/icons-react';
import { useExtraction } from '@hooks/useContext';
import type { IExtractedNodeViewModel } from '@models/index';
import { SupportedDownloadFileType } from '@models/index';
import MemoizedHierarchicalNode from '@src/features/HierarchicalNode/HierarchicalNode';
import OpenMappingControl from '@src/features/MappingStatus/OpenMappingControl';
import RunMappingControl from '@src/features/MappingStatus/RunMappingControl';
import './ExtractionResults.scss';

const ExtractionResults: React.FC = () => {
    const {
        selectNode,
        downloadResult,
        extractedResult,
        selectedNode,
        isLoadingResult,
        doctypeCode,
        selectedProcessResultInfo,
        selectedProcessItem,
        showSuccess,
        showError
    } = useExtraction();

    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    const [isDownloading, setIsDownloading] = useState(false);

    const collectNodeIds = (nodes: IExtractedNodeViewModel[]): string[] => {
        return nodes.flatMap(node => [
            node.id,
            ...(node.children ? collectNodeIds(node.children) : [])
        ]);
    };

    useEffect(() => {
        if (extractedResult?.displayNode) {
            const allIds = collectNodeIds(extractedResult.displayNode);
            setExpandedNodes(new Set(allIds));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extractedResult]);

    const toggleNode = useCallback((nodeId: string) => {
        setExpandedNodes(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(nodeId)) {
                newExpanded.delete(nodeId);
            } else {
                newExpanded.add(nodeId);
            }
            return newExpanded;
        });
    }, []);

    const copyToClipboard = useCallback((text?: string) => {
        if (text) {
            navigator.clipboard.writeText(text);
        }
    }, []);

    const handleDownload = async (fileType: SupportedDownloadFileType) => {
        if (!selectedProcessResultInfo || !selectedProcessItem || !doctypeCode) {
            return;
        }

        try {
            setIsDownloading(true);
            await downloadResult(selectedProcessResultInfo.id, selectedProcessItem.id, fileType);
        } catch (err) {
            console.error('Download failed:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    const getFileTypeIcon = (fileType: SupportedDownloadFileType) => {
        switch (fileType) {
            case SupportedDownloadFileType.Excel:
                return <IconTable size={16} />;
            case SupportedDownloadFileType.Json:
                return <IconBraces size={16} />;
            default:
                return <IconDownload size={16} />;
        }
    };

    const getFileTypeLabel = (fileType: SupportedDownloadFileType) => {
        switch (fileType) {
            case SupportedDownloadFileType.Excel:
                return 'Excel (.xlsx)';
            case SupportedDownloadFileType.Json:
                return 'JSON (.json)';
            default:
                return fileType;
        }
    };

    const canDownload = selectedProcessResultInfo &&
        extractedResult &&
        !isLoadingResult &&
        doctypeCode;

    return (
        <Card className="extraction-results">
            <Group justify="space-between">
                <Text size="lg" fw={600} component="h2">
                    Extracted Results
                </Text>

                <Group gap="sm" className="export-controls" wrap="nowrap">
                    <OpenMappingControl
                        processId={selectedProcessResultInfo?.id ?? ''}
                        processItemId={selectedProcessItem?.id ?? ''}
                        enabled={Boolean(
                            selectedProcessResultInfo?.id &&
                            selectedProcessItem?.id &&
                            !isLoadingResult &&
                            selectedProcessResultInfo?.mappingEnabled
                        )}
                    />
                    <RunMappingControl
                        processId={selectedProcessResultInfo?.id ?? ''}
                        processItemId={selectedProcessItem?.id ?? ''}
                        doctypeCode={doctypeCode}
                        mappingAlreadyEnabled={Boolean(
                            selectedProcessResultInfo?.mappingEnabled
                        )}
                        enabled={Boolean(
                            selectedProcessResultInfo?.id &&
                            selectedProcessItem?.id &&
                            !isLoadingResult &&
                            canDownload
                        )}
                        onSuccess={showSuccess}
                        onError={showError}
                    />
                    <Menu position="bottom-end" portalProps={{ target: '#extraction-portal-root' }}>
                        <Menu.Target>
                            <Button
                                size="xs"
                                variant="outline"
                                leftSection={<IconDownload size={16} />}
                                rightSection={<IconChevronDown size={14} />}
                                disabled={!canDownload || isDownloading}
                                className="export-button"
                            >
                                {isDownloading ? 'Downloading...' : 'Export'}
                            </Button>
                        </Menu.Target>

                        <Menu.Dropdown>
                            {Object.values(SupportedDownloadFileType).map((fileType) => (
                                <Menu.Item
                                    key={fileType}
                                    leftSection={getFileTypeIcon(fileType)}
                                    onClick={() => handleDownload(fileType)}
                                    disabled={isDownloading}
                                >
                                    {getFileTypeLabel(fileType)}
                                </Menu.Item>
                            ))}
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </Group>
            <Box style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {
                    isLoadingResult ?
                        <div className="spinner-container">
                            <Loader size={48} />
                        </div>
                        : <Box className="results-scrollable-container">
                            {extractedResult && (
                                <div>
                                    {extractedResult.displayNode.map((node) =>
                                        <MemoizedHierarchicalNode
                                            key={node.id}
                                            node={node}
                                            depth={0}
                                            isExpanded={expandedNodes.has(node.id)}
                                            isSelected={selectedNode?.id === node.id}
                                            expandedNodes={expandedNodes}
                                            selectedNode={selectedNode}
                                            onToggle={toggleNode}
                                            onSelect={selectNode}
                                            onCopy={copyToClipboard}
                                        />
                                    )}
                                </div>
                            )}
                        </Box>
                }
            </Box>
        </Card>
    );
};

export default ExtractionResults;
